import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DIALOG_TITLE_CLASS, DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { type LightboxItem, PhotoLightboxComponent } from '@lite-workspace/shared/photo-lightbox';
import type { MyTemplate } from '../domain/entities/design.models';

export interface TemplateGrantedDialogContext {
  template: MyTemplate;
  /** Развороты пака уже прочитаны запускающей службой: окно открывается собранным, а не дорастает
   *  миниатюрами под курсором. Пусто — полосы миниатюр нет вовсе. */
  items: readonly LightboxItem[];
}

/** The photographer asked for «Перейти к шаблону» — the caller opens the pack's viewer in its place. */
export type TemplateGrantedResult = 'template' | undefined;

const THUMBNAIL_COUNT = 4;

/**
 * The window that announces a confirmed licence. It opens by itself on the visit after the studio
 * confirmed the payment, so it says what arrived and on what terms — a purchase joins the library, a
 * rental spends one credit on one project — and offers the pack's spreads right away.
 */
@Component({
  selector: 'app-template-granted-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBadge, HlmButton, DialogSheetComponent, PhotoLightboxComponent, BrnDialogTitle],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <!-- The cover bleeds to the dialog edge: negative margins undo the sheet's padding on phones
             and the dialog's own on desktop. -->
        <div class="relative -mx-1 -mt-1 mb-5.5 aspect-video overflow-hidden bg-bg md:-mx-9 md:-mt-9">
          @if (template.coverUrl) {
            <img
              [src]="template.coverUrl"
              alt=""
              class="absolute inset-0 h-full w-full cursor-pointer object-cover"
              (click)="openLightbox(0)"
            />
          }
        </div>

        <h2 brnDialogTitle [class]="titleClass">{{ title }}</h2>

        <div class="-mt-1 mb-3.5 flex items-center gap-2.5">
          <span hlmBadge [variant]="badge.variant">{{ badge.label }}</span>
          <span class="text-sm leading-relaxed text-c2">{{ badge.caption }}</span>
        </div>

        @if (thumbnails.length) {
          <div class="mt-4.5 grid grid-cols-4 gap-2">
            @for (thumbnail of thumbnails; track thumbnail.index) {
              <img
                [src]="thumbnail.src"
                alt=""
                loading="lazy"
                class="aspect-4/3 w-full cursor-pointer rounded-[var(--r-s)] bg-bg object-cover"
                (click)="openLightbox(thumbnail.index)"
              />
            }
          </div>
        }

        <div class="mt-7 flex gap-3">
          <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="close()">Закрыть</button>
          <button hlmBtn type="button" class="h-12 flex-1 justify-center" (click)="openTemplate()">Перейти к шаблону</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>

    @if (lightboxIndex() !== null) {
      <lib-photo-lightbox
        [photos]="items"
        [index]="lightboxIndex() ?? 0"
        [canDownload]="false"
        [maxStage]="'full'"
        (indexChange)="lightboxIndex.set($event)"
        (viewerDismissed)="lightboxIndex.set(null)"
      />
    }
  `,
})
export class TemplateGrantedDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('l', 'p-9') + ' dlg-desktop:overflow-hidden';
  protected readonly titleClass = DIALOG_TITLE_CLASS;
  private readonly dialogRef = inject(BrnDialogRef);

  private readonly ctx = injectBrnDialogContext<TemplateGrantedDialogContext>();
  protected readonly template = this.ctx.template;
  // Копия, а не сам контекст: просмотрщик принимает изменяемый список.
  protected readonly items: LightboxItem[] = [...this.ctx.items];

  protected readonly title =
    this.template.type === 'purchase' ? `Шаблон «${this.template.name}» куплен` : `Шаблон «${this.template.name}» добавлен`;

  // Two elements, never one sentence: the badge names WHAT arrived, the caption its terms.
  protected readonly badge =
    this.template.type === 'purchase'
      ? { label: 'Куплен', variant: 'ok' as const, caption: 'добавлен в библиотеку' }
      : { label: '1 кредит', variant: 'acc' as const, caption: 'один шаблон на один проект' };

  protected readonly lightboxIndex = signal<number | null>(null);

  /** The cover already fills the top of the window, so the strip starts at the spread after it. */
  protected readonly thumbnails = this.items
    .slice(1, THUMBNAIL_COUNT + 1)
    .map((item, offset) => ({ src: item.thumbSrc ?? item.src, index: offset + 1 }));

  protected openLightbox(index: number): void {
    if (index >= this.items.length) return;
    this.lightboxIndex.set(index);
  }

  /** Closing IS the read receipt, but the window does not file it — the launcher that opened it owns
   *  both close paths and confirms the licence against the server before dropping it locally. */
  protected close(): void {
    this.dialogRef.close(undefined);
  }

  protected openTemplate(): void {
    this.dialogRef.close('template');
  }
}
